import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  Eye,
  Settings,
  Users,
  MessageSquare,
  Calendar,
  ArrowLeft,
  MoreHorizontal,
  CheckSquare,
  Square,
} from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { campaignsApi, Campaign } from "@/lib/campaignsApi";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Pagination from "@/components/ui/pagination";

const AdminCampaignModeration: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({
    status: "all",
    searchTerm: "",
  });
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [moderationDialog, setModerationDialog] = useState<{
    open: boolean;
    campaignId?: string;
    action?: "SUSPEND" | "REACTIVATE" | "DELETE";
    reason: string;
  }>({
    open: false,
    reason: "",
  });
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    open: boolean;
    action?: "SUSPEND" | "REACTIVATE" | "DELETE";
    reason: string;
  }>({
    open: false,
    reason: "",
  });

  // Check if user is admin
  const isAdmin = isAuthenticated && user?.role === "ADMIN";

  // Fetch campaigns for moderation
  const {
    data: campaignsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "campaigns-moderation",
      page,
      limit,
      filters.searchTerm,
      filters.status,
    ],
    queryFn: () =>
      campaignsApi.getCampaignsForModeration(
        page,
        limit,
        filters.searchTerm,
        filters.status
      ),
    enabled: isAdmin,
  });

  const campaigns = campaignsData?.campaigns || [];
  const pagination = campaignsData?.pagination;

  // Moderation mutation
  const moderateCampaignMutation = useMutation({
    mutationFn: ({
      campaignId,
      action,
      reason,
    }: {
      campaignId: string;
      action: "SUSPEND" | "REACTIVATE" | "DELETE";
      reason?: string;
    }) => campaignsApi.moderateCampaign(campaignId, action, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns-moderation"] });
      toast({
        title: "Campaign moderated",
        description: "The campaign has been successfully moderated.",
      });
      setModerationDialog({ open: false, reason: "" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to moderate campaign. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Bulk moderation mutation
  const bulkModerateMutation = useMutation({
    mutationFn: ({
      campaignIds,
      action,
      reason,
    }: {
      campaignIds: string[];
      action: "SUSPEND" | "REACTIVATE" | "DELETE";
      reason?: string;
    }) => campaignsApi.bulkModerateCampaigns(campaignIds, action, reason),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns-moderation"] });
      setSelectedCampaigns([]);
      toast({
        title: "Bulk action completed",
        description: `${data.totalCampaigns} campaigns have been processed.`,
      });
      setBulkActionDialog({ open: false, reason: "" });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to perform bulk action. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1);
  }, [filters.searchTerm, filters.status]);

  // Handle individual campaign moderation
  const handleModerateCampaign = (
    campaignId: string,
    action: "SUSPEND" | "REACTIVATE" | "DELETE"
  ) => {
    setModerationDialog({
      open: true,
      campaignId,
      action,
      reason: "",
    });
  };

  // Handle bulk action
  const handleBulkAction = (action: "SUSPEND" | "REACTIVATE" | "DELETE") => {
    if (selectedCampaigns.length === 0) {
      toast({
        title: "No campaigns selected",
        description:
          "Please select at least one campaign to perform bulk action.",
        variant: "destructive",
      });
      return;
    }
    setBulkActionDialog({
      open: true,
      action,
      reason: "",
    });
  };

  // Execute moderation action
  const executeModeration = () => {
    if (moderationDialog.campaignId && moderationDialog.action) {
      moderateCampaignMutation.mutate({
        campaignId: moderationDialog.campaignId,
        action: moderationDialog.action,
        reason: moderationDialog.reason || undefined,
      });
    }
  };

  // Execute bulk action
  const executeBulkAction = () => {
    if (bulkActionDialog.action) {
      bulkModerateMutation.mutate({
        campaignIds: selectedCampaigns,
        action: bulkActionDialog.action,
        reason: bulkActionDialog.reason || undefined,
      });
    }
  };

  // Toggle campaign selection
  const toggleCampaignSelection = (campaignId: string) => {
    setSelectedCampaigns((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  // Toggle all campaigns selection
  const toggleAllCampaigns = () => {
    if (selectedCampaigns.length === campaigns.length) {
      setSelectedCampaigns([]);
    } else {
      setSelectedCampaigns(campaigns.map((c) => c.id));
    }
  };

  // Get status badge variant
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return (
          <Badge variant="default" className="bg-green-100 text-green-800">
            Active
          </Badge>
        );
      case "PAUSED":
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
            Paused
          </Badge>
        );
      case "SUSPENDED":
        return (
          <Badge variant="destructive" className="bg-red-100 text-red-800">
            Suspended
          </Badge>
        );
      case "DRAFT":
        return (
          <Badge variant="outline" className="bg-gray-100 text-gray-800">
            Draft
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Get action button
  const getActionButton = (
    campaign: Campaign,
    action: "SUSPEND" | "REACTIVATE" | "DELETE"
  ) => {
    const actionConfig = {
      SUSPEND: {
        icon: <Pause className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: "Suspend",
        variant: "destructive" as const,
        disabled: campaign.status === "SUSPENDED",
      },
      REACTIVATE: {
        icon: <Play className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: "Reactivate",
        variant: "default" as const,
        disabled: campaign.status === "ACTIVE",
      },
      DELETE: {
        icon: <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />,
        label: "Delete",
        variant: "destructive" as const,
        disabled: false,
      },
    };

    const config = actionConfig[action as keyof typeof actionConfig];
    if (!config) return null;

    return (
      <Button
        variant={config.variant}
        size="sm"
        disabled={config.disabled}
        onClick={() => handleModerateCampaign(campaign.id, action)}
        className="text-xs sm:text-sm"
      >
        {config.icon}
        <span className="hidden sm:inline">{config.label}</span>
      </Button>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                Please log in to access the campaign moderation panel.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
        <NavBar />
        <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                Access Denied
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                You need admin privileges to access the campaign moderation
                panel.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      <NavBar />
      <div className="container mx-auto px-4 pt-20 sm:pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6 sm:mb-8"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-r from-red-500 to-orange-500">
                <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                  Campaign Moderation
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground mt-1">
                  Monitor and moderate campaigns to maintain platform quality
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoading}
                className="text-xs sm:text-sm"
              >
                <RefreshCw
                  className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${
                    isLoading ? "animate-spin" : ""
                  }`}
                />
                <span className="hidden sm:inline">Refresh</span>
                <span className="sm:hidden">Refresh</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin")}
                className="text-xs sm:text-sm"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to Admin</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </div>
          </motion.div>

          {/* Filters and Bulk Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mb-4 sm:mb-6"
          >
            <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between">
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 flex-1 w-full">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Search campaigns..."
                        value={filters.searchTerm}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            searchTerm: e.target.value,
                          }))
                        }
                        className="pl-10"
                      />
                    </div>
                    <Select
                      value={filters.status}
                      onValueChange={(value) =>
                        setFilters((prev) => ({ ...prev, status: value }))
                      }
                    >
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="PAUSED">Paused</SelectItem>
                        <SelectItem value="SUSPENDED">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-wrap gap-2 w-full xl:w-auto">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleBulkAction("SUSPEND")}
                      disabled={selectedCampaigns.length === 0}
                      className="text-xs sm:text-sm flex-1 sm:flex-none"
                    >
                      <Pause className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Suspend Selected</span>
                      <span className="sm:hidden">Suspend</span>
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleBulkAction("REACTIVATE")}
                      disabled={selectedCampaigns.length === 0}
                      className="text-xs sm:text-sm flex-1 sm:flex-none"
                    >
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">
                        Reactivate Selected
                      </span>
                      <span className="sm:hidden">Reactivate</span>
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={selectedCampaigns.length === 0}
                          className="text-xs sm:text-sm flex-1 sm:flex-none"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          <span className="hidden sm:inline">
                            Delete Selected
                          </span>
                          <span className="sm:hidden">Delete</span>
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Campaigns</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete{" "}
                            {selectedCampaigns.length} campaign(s)? This action
                            cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleBulkAction("DELETE")}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Campaigns Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-sm">
              <CardHeader className="bg-gradient-to-r from-muted/50 to-transparent border-b border-border/50">
                <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <span className="text-lg sm:text-xl">
                    Campaigns ({pagination?.total || campaigns.length})
                  </span>
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                    <span>{selectedCampaigns.length} selected</span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8 sm:w-12">
                          <Checkbox
                            checked={
                              selectedCampaigns.length === campaigns.length &&
                              campaigns.length > 0
                            }
                            onCheckedChange={toggleAllCampaigns}
                          />
                        </TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm">
                          Campaign
                        </TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm hidden sm:table-cell">
                          Creator
                        </TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm">
                          Status
                        </TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm hidden lg:table-cell">
                          Engagement
                        </TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm hidden lg:table-cell">
                          Created
                        </TableHead>
                        <TableHead className="font-semibold text-xs sm:text-sm">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-6 sm:py-8"
                          >
                            <div className="flex items-center justify-center">
                              <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 animate-spin mr-2" />
                              <span className="text-sm sm:text-base">
                                Loading campaigns...
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : error ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-6 sm:py-8"
                          >
                            <div className="text-red-600 text-sm sm:text-base">
                              Error loading campaigns. Please try again.
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : campaigns.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-6 sm:py-8"
                          >
                            <div className="text-muted-foreground text-sm sm:text-base">
                              No campaigns found matching your criteria.
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        campaigns.map((campaign, index) => (
                          <motion.tr
                            key={campaign.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="hover:bg-muted/20 transition-colors"
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedCampaigns.includes(
                                  campaign.id
                                )}
                                onCheckedChange={() =>
                                  toggleCampaignSelection(campaign.id)
                                }
                              />
                            </TableCell>
                            <TableCell className="font-medium text-xs sm:text-sm">
                              <div>
                                <div className="font-semibold">
                                  {campaign.title}
                                </div>
                                <div className="text-xs sm:text-sm text-muted-foreground">
                                  <span className="sm:hidden">
                                    {campaign.creator.name} •{" "}
                                  </span>
                                  {campaign.tagline}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs sm:text-sm hidden sm:table-cell">
                              {campaign.creator.name}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(campaign.status)}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <div className="flex items-center gap-4 text-xs sm:text-sm">
                                <div className="flex items-center gap-1">
                                  <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                                  {campaign._count.responses}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                                  {campaign._count.comments}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs sm:text-sm hidden lg:table-cell">
                              {new Date(
                                campaign.createdAt
                              ).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 sm:gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    navigate(`/campaign/${campaign.id}`)
                                  }
                                  className="text-xs sm:text-sm"
                                >
                                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span className="hidden sm:inline">View</span>
                                </Button>
                                {getActionButton(campaign, "SUSPEND")}
                                {getActionButton(campaign, "REACTIVATE")}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      className="text-xs sm:text-sm"
                                    >
                                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                                      <span className="hidden sm:inline">
                                        Delete
                                      </span>
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Delete Campaign
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to delete "
                                        {campaign.title}"? This action cannot be
                                        undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Cancel
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() =>
                                          handleModerateCampaign(
                                            campaign.id,
                                            "DELETE"
                                          )
                                        }
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </motion.tr>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
              {pagination && (
                <div className="p-4 sm:p-6 border-t border-border/50">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    totalItems={pagination.total}
                    itemsPerPage={pagination.limit}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Moderation Dialog */}
      <Dialog
        open={moderationDialog.open}
        onOpenChange={(open) =>
          setModerationDialog((prev) => ({ ...prev, open }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {moderationDialog.action === "SUSPEND" && "Suspend Campaign"}
              {moderationDialog.action === "REACTIVATE" &&
                "Reactivate Campaign"}
              {moderationDialog.action === "DELETE" && "Delete Campaign"}
            </DialogTitle>
            <DialogDescription>
              {moderationDialog.action === "SUSPEND" &&
                "This will suspend the campaign and hide it from public view."}
              {moderationDialog.action === "REACTIVATE" &&
                "This will reactivate the campaign and make it visible again."}
              {moderationDialog.action === "DELETE" &&
                "This will permanently delete the campaign. This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                placeholder="Enter a reason for this action..."
                value={moderationDialog.reason}
                onChange={(e) =>
                  setModerationDialog((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setModerationDialog({ open: false, reason: "" })}
            >
              Cancel
            </Button>
            <Button
              variant={
                moderationDialog.action === "DELETE" ? "destructive" : "default"
              }
              onClick={executeModeration}
              disabled={moderateCampaignMutation.isPending}
            >
              {moderateCampaignMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {moderationDialog.action === "SUSPEND" && "Suspend"}
              {moderationDialog.action === "REACTIVATE" && "Reactivate"}
              {moderationDialog.action === "DELETE" && "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkActionDialog.open}
        onOpenChange={(open) =>
          setBulkActionDialog((prev) => ({ ...prev, open }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkActionDialog.action === "SUSPEND" && "Suspend Campaigns"}
              {bulkActionDialog.action === "REACTIVATE" &&
                "Reactivate Campaigns"}
              {bulkActionDialog.action === "DELETE" && "Delete Campaigns"}
            </DialogTitle>
            <DialogDescription>
              This action will be applied to {selectedCampaigns.length} selected
              campaign(s).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea
                placeholder="Enter a reason for this action..."
                value={bulkActionDialog.reason}
                onChange={(e) =>
                  setBulkActionDialog((prev) => ({
                    ...prev,
                    reason: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkActionDialog({ open: false, reason: "" })}
            >
              Cancel
            </Button>
            <Button
              variant={
                bulkActionDialog.action === "DELETE" ? "destructive" : "default"
              }
              onClick={executeBulkAction}
              disabled={bulkModerateMutation.isPending}
            >
              {bulkModerateMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {bulkActionDialog.action === "SUSPEND" && "Suspend All"}
              {bulkActionDialog.action === "REACTIVATE" && "Reactivate All"}
              {bulkActionDialog.action === "DELETE" && "Delete All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCampaignModeration;
